/**
 * The work timeline, laid out: one lane per agent (or per repository), a bar
 * per run, a time axis that suits the range, a minimap and a zoom window
 * over it, and every sentence the timeline says. DOM-free and React-free,
 * so the tests drive it directly (test/workTimelineModel.test.ts).
 *
 * The record is `shared/schemas/projectRuns.ts` and the view of it
 * `shared/protocol/workTimeline.ts`. Both carry zod, which the webview's
 * tests cannot load by value, so only their types come in here and the few
 * small rules this needs (`runMatches`, `runTotals`, the words) are
 * restated — the real ones decide what the desktop and the hosts answer.
 *
 * Words for people: run, asked by a person, handed over by an agent,
 * automation, cancelled, run time, tokens used.
 */
import type { WorkRunView } from '@aioe/shared/protocol/workTimeline';
import type { RunFilter, RunOutcome, RunTotals, RunTrigger, WorkRun } from '@aioe/shared';
import { dateMedium, dateTime, time, weekday } from '@aioe/shared/wording/dateTime';
import { formatCost } from '../../workbench/moneyFormat.ts';
import { planIdentity } from '../../workbench/planTones.ts';

export type { RunFilter, RunTotals, WorkRun, WorkRunView };

export const HOUR = 3_600_000;
export const DAY = 86_400_000;
/** The longest range a timeline asks for (WORK_TIMELINE_MAX_RANGE_MS). */
export const MAX_RANGE_MS = 92 * DAY;
/** The shortest span zooming in may show. */
export const MIN_SPAN_MS = 10 * 60_000;
/** A run shorter than this many pixels is still drawn this wide, so it can be seen and pointed at; its hover gives the real times. */
export const MIN_BAR_PX = 6;
/** Lanes beyond this are windowed: only those in view (and a few either side) are drawn. */
export const VIRTUALISE_AFTER = 40;

export const TRIGGER_TEXT: Record<RunTrigger, string> = { person: 'Asked by a person', agent: 'Handed over by an agent', automation: 'Automation' };
export const OUTCOME_TEXT: Record<RunOutcome, string> = { ok: 'Finished', error: 'Failed', cancelled: 'Cancelled' };

// ── Runs as the timeline draws them ─────────────────────────────────────

/** A run with what the timeline needs, whichever answer it came in (`runs-state` or `workTimeline.get`). */
export type TimelineRun = WorkRunView;

const ms = (iso: string | undefined): number => (iso ? Date.parse(iso) : Number.NaN);

/** Fill in what a repository's answer leaves out: its repository's name, how long, whether it is still going. */
export function asTimelineRuns(runs: ReadonlyArray<WorkRun | WorkRunView>, at: number, repositoryName?: (rootId: string) => string): TimelineRun[] {
  return runs.map((r) => {
    const v = r as Partial<WorkRunView> & WorkRun;
    const live = v.live ?? !r.endedAt;
    const start = ms(r.startedAt);
    const end = r.endedAt ? ms(r.endedAt) : at;
    const durationMs = v.durationMs ?? (Number.isFinite(start) && Number.isFinite(end) ? Math.max(0, end - start) : 0);
    return { ...r, repositoryName: v.repositoryName ?? repositoryName?.(r.rootId) ?? (r.rootId || 'this repository'), durationMs, live } as TimelineRun;
  });
}

/** Where a bar starts and ends (ms): a run still going reaches `now`. */
export function runSpan(run: Pick<WorkRun, 'startedAt' | 'endedAt'>, now: number): { start: number; end: number } {
  const start = ms(run.startedAt);
  const end = run.endedAt ? ms(run.endedAt) : Math.max(now, start);
  return { start, end: Number.isFinite(end) ? end : start };
}

/** `runMatches` (projectRuns.ts), restated for the harness and the tests. */
export function matchesFilter(run: WorkRun, f: RunFilter | undefined): boolean {
  if (!f) return true;
  if (f.rootId !== undefined && run.rootId !== f.rootId) return false;
  if (f.agent !== undefined && run.agent !== f.agent) return false;
  if (f.teamId !== undefined && run.teamId !== f.teamId) return false;
  if (f.sessionId !== undefined && run.sessionId !== f.sessionId) return false;
  if (f.trigger !== undefined && run.trigger !== f.trigger) return false;
  if (f.task) {
    if (!run.task) return false;
    if (f.task.uid !== undefined) {
      if (run.task.uid !== f.task.uid) return false;
    } else if ((f.task.board !== undefined && run.task.board !== f.task.board) || (f.task.id !== undefined && run.task.id !== f.task.id)) return false;
  }
  if (f.plan && (run.plan?.id !== f.plan.id || (f.plan.step !== undefined && run.plan.step !== f.plan.step))) return false;
  if (f.roadmap) {
    const want = f.roadmap;
    if (!(run.roadmap ?? []).some((r) => r.rootId === want.rootId && r.id === want.id)) return false;
  }
  if (f.projectTask !== undefined && run.projectTask?.uid !== f.projectTask) return false;
  return true;
}

/** `runTotals` (projectRuns.ts), restated: the numbers over the runs a filter chip left. */
export function totalsOf(runs: ReadonlyArray<WorkRun>, now: number): RunTotals {
  const agents = new Set<string>();
  let runTimeMs = 0;
  let tokens = 0;
  let costUsd = 0;
  let cancelled = 0;
  let failed = 0;
  for (const r of runs) {
    agents.add(`${r.rootId}\u0000${r.teamId}\u0000${r.agent}`);
    const { start, end } = runSpan(r, now);
    runTimeMs += Number.isFinite(start) ? Math.max(0, end - start) : 0;
    tokens += (r.tokens?.input ?? 0) + (r.tokens?.output ?? 0);
    costUsd += r.costUsd ?? 0;
    if (r.outcome === 'cancelled') cancelled++;
    if (r.outcome === 'error') failed++;
  }
  return { runs: runs.length, agents: agents.size, runTimeMs, tokens, costUsd, cancelled, failed };
}

// ── Words and numbers ───────────────────────────────────────────────────

/** "2h 56m", "42m", "35s", "0s". */
export function formatRunTime(msIn: number): string {
  const s = Math.max(0, Math.round((Number.isFinite(msIn) ? msIn : 0) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

/** "18.5M", "12.4k", "940". */
export function formatTokenCount(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1_000) return String(Math.round(n));
  if (n < 1_000_000) return `${(n / 1_000).toFixed(n < 10_000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

/** A US-dollar cost in the money shown (../../moneyFormat.ts): "A$0.63", "US$0.0040", "US$0.00". */
export function formatUsd(usd: number): string {
  return formatCost(usd <= 0 ? 0 : usd);
}

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

export interface TimelineKpi {
  id: 'runs' | 'agents' | 'runTime' | 'tokens' | 'cost';
  label: string;
  value: string;
  detail: string;
}

// ── Rebuilt runs (D108) ─────────────────────────────────────────────

/** What the timeline says when some of its runs were rebuilt rather than recorded. */
export const REBUILT_NOTE = 'Some of this was rebuilt from the board and agent history; times are approximate.';

/** Whether a run was rebuilt from the board and the agents' history (work before the run log, or its gaps). */
export function isRebuiltRun(run: object): boolean {
  return (run as { rebuilt?: unknown }).rebuilt === true;
}

/**
 * Where the work itself happened: from the first run's start to the last
 * one's end (now, while one is still going), with a little room either
 * side, inside `full`. Undefined with no runs, or when it is the whole range
 * anyway. A plan's timeline opens on this, not on the days around it.
 */
export function activeWindow(runs: ReadonlyArray<Pick<WorkRun, 'startedAt' | 'endedAt'>>, now: number, full: TimeWindow): TimeWindow | undefined {
  let start = Number.POSITIVE_INFINITY;
  let end = Number.NEGATIVE_INFINITY;
  for (const r of runs) {
    const s = runSpan(r, now);
    if (!Number.isFinite(s.start)) continue;
    start = Math.min(start, s.start);
    end = Math.max(end, s.end);
  }
  if (!Number.isFinite(start) || !Number.isFinite(end)) return undefined;
  const pad = Math.max(30 * 60_000, (end - start) * 0.04);
  let a = Math.max(full.start, start - pad);
  let b = Math.min(full.end, end + pad);
  if (b - a < MIN_SPAN_MS * 3) {
    const mid = (a + b) / 2;
    a = Math.max(full.start, mid - MIN_SPAN_MS * 1.5);
    b = Math.min(full.end, mid + MIN_SPAN_MS * 1.5);
  }
  if (b <= a || (a <= full.start && b >= full.end)) return undefined;
  return { start: a, end: b };
}

/**
 * The strip above the lanes. Tokens and cost count what was recorded: a
 * rebuilt run usually has neither, so when nothing in view recorded them
 * the card says "—" rather than a zero, and when only some did it says
 * so. Cost shows only when a run carries one, or rebuilt runs are in view.
 */
export function timelineKpis(totals: RunTotals, runs: ReadonlyArray<Pick<WorkRun, 'costUsd' | 'tokens'> & { rebuilt?: unknown }>): TimelineKpi[] {
  const rebuilt = runs.filter((r) => isRebuiltRun(r)).length;
  const withTokens = runs.filter((r) => r.tokens && r.tokens.input + r.tokens.output > 0).length;
  const withCost = runs.some((r) => typeof r.costUsd === 'number');
  const runsDetail = totals.cancelled || totals.failed ? [totals.failed ? `${totals.failed} failed` : '', totals.cancelled ? `${totals.cancelled} cancelled` : ''].filter(Boolean).join(' · ') : 'none failed or cancelled';
  const out: TimelineKpi[] = [
    { id: 'runs', label: 'Runs', value: totals.runs.toLocaleString(), detail: rebuilt ? `${rebuilt.toLocaleString()} rebuilt from history` : runsDetail },
    { id: 'agents', label: 'Agents', value: totals.agents.toLocaleString(), detail: 'worked in this range' },
    { id: 'runTime', label: 'Run time', value: formatRunTime(totals.runTimeMs), detail: rebuilt ? 'added up; approximate' : 'added up across agents' },
    rebuilt && !withTokens
      ? { id: 'tokens', label: 'Tokens used', value: '—', detail: 'not recorded for this work' }
      : { id: 'tokens', label: 'Tokens used', value: formatTokenCount(totals.tokens), detail: rebuilt && withTokens < runs.length ? 'where recorded' : 'input and output' },
  ];
  if (withCost) out.push({ id: 'cost', label: 'Cost', value: formatUsd(totals.costUsd), detail: rebuilt ? 'where recorded' : 'as the runs report it' });
  else if (rebuilt) out.push({ id: 'cost', label: 'Cost', value: '—', detail: 'not recorded for this work' });
  return out;
}

/** Home's sentence: "Today: 18 runs by 8 agents, 2h 56m, 18.5M tokens". */
export function todaySentence(totals: RunTotals): string {
  if (!totals.runs) return 'Today: no runs yet.';
  return `Today: ${plural(totals.runs, 'run')} by ${plural(totals.agents, 'agent')}, ${formatRunTime(totals.runTimeMs)}, ${formatTokenCount(totals.tokens)} tokens`;
}

/** The office overlay's line for one agent: "Today: 3 runs · 42 min". */
export function overlayLine(runs: number, runTimeMs: number): string {
  if (!runs) return 'Today: no runs yet';
  const m = Math.round(runTimeMs / 60_000);
  const time = m < 1 ? 'under a minute' : m < 60 ? `${m} min` : formatRunTime(runTimeMs);
  return `Today: ${plural(runs, 'run')} · ${time}`;
}

/** "07/09/2026, 2:49 pm", in local time: the person's own date order and clock. */
export function formatStamp(t: number): string {
  return dateTime(t);
}

/** The footer: "43 runs · 01/09/2026 00:00 to 07/09/2026 14:49". A range that runs on past now ends at now. */
export function footerText(count: number, from: number, to: number, now: number): string {
  const end = Math.min(to, Math.max(from, now));
  return `${plural(count, 'run')} · ${formatStamp(from)} to ${formatStamp(end)}`;
}

// ── The office overlay ──────────────────────────────────────────────

export interface TodayRuns {
  byAgent: ReadonlyMap<string, { runs: number; runTimeMs: number }>;
  loaded: boolean;
}

/** Today's runs by agent: how many, and how long they added up to. */
export function todayByAgent(runs: readonly TimelineRun[], now: number, from: number): Map<string, { runs: number; runTimeMs: number }> {
  const out = new Map<string, { runs: number; runTimeMs: number }>();
  for (const r of runs) {
    const { start, end } = runSpan(r, now);
    const cur = out.get(r.agent) ?? { runs: 0, runTimeMs: 0 };
    cur.runs += 1;
    cur.runTimeMs += Math.max(0, end - Math.max(start, from));
    out.set(r.agent, cur);
  }
  return out;
}

/** The overlay's line for one agent; nothing while today's runs are still being read. */
export function agentTodayLine(today: TodayRuns, agent: string): string {
  if (!today.loaded) return '';
  const t = today.byAgent.get(agent);
  return overlayLine(t?.runs ?? 0, t?.runTimeMs ?? 0);
}

// ── Ranges ──────────────────────────────────────────────────────────────

export type RangePreset = 'today' | '7d' | '30d' | 'custom';

export const RANGE_PRESETS: ReadonlyArray<{ id: Exclude<RangePreset, 'custom'>; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
];

export function startOfDay(t: number): number {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** The next local midnight after `t` (a DST day is 23 or 25 hours long). */
export function endOfDay(t: number): number {
  const d = new Date(startOfDay(t));
  d.setDate(d.getDate() + 1);
  return d.getTime();
}

/** A preset as a range: whole local days, ending at the end of today so the Now line shows. */
export function presetRange(preset: Exclude<RangePreset, 'custom'>, now: number): { from: number; to: number } {
  const to = endOfDay(now);
  const days = preset === 'today' ? 1 : preset === '7d' ? 7 : 30;
  const d = new Date(startOfDay(now));
  d.setDate(d.getDate() - (days - 1));
  return { from: d.getTime(), to };
}

/** The Range control's key for a range that is none of the presets: no item has it, so none is highlighted. */
export const NO_PRESET_KEY = 'custom';

/**
 * The key the Range control shows selected: the preset; for a custom range,
 * the preset it is exactly (the last seven whole days picked by date is
 * "7 days"), else {@link NO_PRESET_KEY}. Never null: HeroUI Pro's Segment
 * reads a null key as uncontrolled and keeps the last item it showed.
 */
export function rangeSegmentKey(preset: RangePreset, from: number, to: number, now: number): string {
  if (preset !== 'custom') return preset;
  const same = RANGE_PRESETS.find((p) => {
    const r = presetRange(p.id, now);
    return r.from === from && r.to === to;
  });
  return same?.id ?? NO_PRESET_KEY;
}

/** A range the desktop will take: the right way round, at least an hour, at most 92 days (the start moves). */
export function clampRange(from: number, to: number): { from: number; to: number } {
  let a = Math.min(from, to);
  let b = Math.max(from, to);
  if (b - a < HOUR) b = a + HOUR;
  if (b - a > MAX_RANGE_MS) a = b - MAX_RANGE_MS;
  return { from: a, to: b };
}

export interface StoredRange {
  preset: RangePreset;
  /** Only for `custom`. */
  from?: number;
  to?: number;
}

export interface RangeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function rangeKey(view: string): string {
  return `mojoup.timeline.range.${view}`;
}

/** What this view last showed; `fallback` until someone chose. Storage that throws reads as nothing chosen. */
export function readRange(storage: RangeStorage | undefined, view: string, fallback: RangePreset = '7d'): StoredRange {
  try {
    const raw = storage?.getItem(rangeKey(view));
    if (!raw) return { preset: fallback };
    const v = JSON.parse(raw) as StoredRange;
    if (v.preset === 'custom' && Number.isFinite(v.from) && Number.isFinite(v.to)) return { preset: 'custom', ...clampRange(v.from as number, v.to as number) };
    if (v.preset === 'today' || v.preset === '7d' || v.preset === '30d') return { preset: v.preset };
  } catch {
    /* fall through */
  }
  return { preset: fallback };
}

export function writeRange(storage: RangeStorage | undefined, view: string, range: StoredRange): void {
  try {
    storage?.setItem(rangeKey(view), JSON.stringify(range));
  } catch {
    /* a private window: the choice lasts as long as the view */
  }
}

/** The range a stored choice means now. */
export function resolveRange(stored: StoredRange, now: number): { from: number; to: number } {
  if (stored.preset === 'custom' && stored.from !== undefined && stored.to !== undefined) return clampRange(stored.from, stored.to);
  return presetRange(stored.preset === 'custom' ? '7d' : stored.preset, now);
}

/** A plan's range: from when it was made to the end of today, at most 92 days back. */
export function sinceRange(createdAt: string | number | undefined, now: number, minDays = 1): { from: number; to: number } {
  const to = endOfDay(now);
  const made = typeof createdAt === 'number' ? createdAt : ms(createdAt);
  const floor = to - minDays * DAY;
  const from = Number.isFinite(made) ? Math.min(startOfDay(made), floor) : floor;
  return clampRange(from, to);
}

// ── Time scale, ticks, zoom ─────────────────────────────────────────────

export interface TimeWindow {
  start: number;
  end: number;
}

/** `x(t)` over `width` pixels for the window, and back. */
export function timeScale(win: TimeWindow, width: number): { x: (t: number) => number; t: (x: number) => number; pxPerMs: number } {
  const span = Math.max(1, win.end - win.start);
  const k = Math.max(0, width) / span;
  return { x: (t) => (t - win.start) * k, t: (x) => win.start + x / (k || 1), pxPerMs: k };
}

const MIN = 60_000;
const STEPS: readonly number[] = [5 * MIN, 10 * MIN, 15 * MIN, 30 * MIN, HOUR, 2 * HOUR, 3 * HOUR, 6 * HOUR, 12 * HOUR, DAY, 2 * DAY, 7 * DAY, 14 * DAY];

export interface AxisTick {
  t: number;
  x: number;
  label: string;
  /** A day boundary: drawn a little stronger. */
  major: boolean;
}

/** The step that keeps ticks at least `minPx` apart. */
export function tickStep(span: number, width: number, minPx = 76): number {
  for (const s of STEPS) if ((s / Math.max(1, span)) * width >= minPx) return s;
  return STEPS[STEPS.length - 1];
}

/** The first tick at or after `start`, on a local boundary of `step`. */
function firstTick(start: number, step: number): number {
  if (step >= DAY) {
    let t = startOfDay(start);
    if (t < start) t = endOfDay(start);
    if (step === 7 * DAY || step === 14 * DAY) {
      // Weeks start on Monday.
      const d = new Date(t);
      const back = (d.getDay() + 6) % 7;
      if (back) {
        d.setDate(d.getDate() + (7 - back));
        t = d.getTime();
      }
    }
    return t;
  }
  const day = startOfDay(start);
  return day + Math.ceil((start - day) / step) * step;
}

function nextTick(t: number, step: number): number {
  if (step < DAY) return t + step;
  const d = new Date(t);
  d.setDate(d.getDate() + Math.round(step / DAY));
  return d.getTime();
}

/** The ticks across a window: hours for a day, days for a week, days or weeks for a month. */
export function axisTicks(win: TimeWindow, width: number): AxisTick[] {
  const span = win.end - win.start;
  if (!(span > 0) || !(width > 0)) return [];
  // A narrow lane (a side pane, a sheet) takes ticks a little closer, so a week still shows its days.
  const step = tickStep(span, width, width < 420 ? 52 : 76);
  const scale = timeScale(win, width);
  const out: AxisTick[] = [];
  for (let t = firstTick(win.start, step), guard = 0; t <= win.end && guard < 400; t = nextTick(t, step), guard++) {
    const d = new Date(t);
    const midnight = d.getHours() === 0 && d.getMinutes() === 0;
    const label = step >= DAY || midnight ? (step >= 7 * DAY || span > 14 * DAY ? dateMedium(t, { year: false }) : `${weekday(t, 'short')} ${d.getDate()}`) : time(t);
    out.push({ t, x: scale.x(t), label, major: midnight });
  }
  return out;
}

/** Zoom by `factor` (>1 in) about `anchor` (ms), kept inside the full range and never narrower than {@link MIN_SPAN_MS}. */
export function zoomWindow(win: TimeWindow, full: TimeWindow, factor: number, anchor = (win.start + win.end) / 2): TimeWindow {
  const fullSpan = full.end - full.start;
  const span = Math.min(fullSpan, Math.max(Math.min(MIN_SPAN_MS, fullSpan), (win.end - win.start) / factor));
  const ratio = (anchor - win.start) / Math.max(1, win.end - win.start);
  return clampWindow({ start: anchor - span * ratio, end: anchor - span * ratio + span }, full);
}

/** Slide the window by `delta` ms, stopping at the ends. */
export function panWindow(win: TimeWindow, full: TimeWindow, delta: number): TimeWindow {
  return clampWindow({ start: win.start + delta, end: win.end + delta }, full);
}

/** The window inside the full range, its span kept. */
export function clampWindow(win: TimeWindow, full: TimeWindow): TimeWindow {
  const span = Math.min(full.end - full.start, Math.max(1, win.end - win.start));
  let start = win.start;
  if (start < full.start) start = full.start;
  if (start + span > full.end) start = full.end - span;
  return { start, end: start + span };
}

/** How far in, as "×3": 1 at the whole range. */
export function zoomLevel(win: TimeWindow, full: TimeWindow): number {
  return (full.end - full.start) / Math.max(1, win.end - win.start);
}

// ── The minimap ─────────────────────────────────────────────────────────

/** The visible window on a minimap `width` wide over the full range. */
export function minimapWindow(win: TimeWindow, full: TimeWindow, width: number): { x: number; w: number } {
  const s = timeScale(full, width);
  const x = Math.max(0, s.x(win.start));
  return { x, w: Math.max(6, Math.min(width, s.x(win.end)) - x) };
}

/** The window after its minimap box was dragged by `dx` pixels. */
export function dragMinimap(win: TimeWindow, full: TimeWindow, width: number, dx: number): TimeWindow {
  return panWindow(win, full, dx / Math.max(1e-9, timeScale(full, width).pxPerMs));
}

/** The window centred where the minimap was clicked. */
export function centreMinimapAt(win: TimeWindow, full: TimeWindow, width: number, x: number): TimeWindow {
  const t = timeScale(full, width).t(x);
  const span = win.end - win.start;
  return clampWindow({ start: t - span / 2, end: t + span / 2 }, full);
}

// ── Lanes and bars ──────────────────────────────────────────────────────

export type LanesBy = 'agent' | 'repository';

export interface TimelineLane {
  key: string;
  label: string;
  /** A second line: the team (and repository), or for a repository lane its agents. */
  sub: string;
  /** An operations manager: it gets the manager's glyph. */
  ops: boolean;
  runs: TimelineRun[];
  /** Each run's row inside the lane, by run id, when runs overlap. */
  rowOf: Map<string, number>;
  rows: number;
  /** When the lane last did anything: lanes are in this order, most recent first. */
  lastAt: number;
  /**
   * A crew member's lane (D178): the plan its crew works on, for the colour
   * dot beside the member's name. The label is the member's own name
   * ("Mira"), never the crew's internal key.
   */
  crew?: { planRef: string; planId?: string; colorIndex?: number };
  /**
   * The lane holds its member's commits as well as their runs (the commit
   * overlay, D180): a thin band under the bars carries the markers, so a
   * marker never sits on a bar.
   */
  commitBand?: boolean;
}

/** Whether a name reads as an operations manager's ("Ops", "Ops manager", "Operations"). */
export function isOpsManagerName(name: string): boolean {
  return /^(ops|operations)(\b|[-_ ]?manager)/i.test(name.trim());
}

/** Greedy interval packing: the first row each run fits in, so overlapping runs of one lane sit one above the other. */
export function packRows(runs: ReadonlyArray<TimelineRun>, now: number, gapMs = 0): { rowOf: Map<string, number>; rows: number } {
  const sorted = [...runs].sort((a, b) => runSpan(a, now).start - runSpan(b, now).start);
  const ends: number[] = [];
  const rowOf = new Map<string, number>();
  for (const r of sorted) {
    const { start, end } = runSpan(r, now);
    let row = ends.findIndex((e) => e + gapMs <= start);
    if (row < 0) row = ends.length;
    ends[row] = end;
    rowOf.set(r.id, row);
  }
  return { rowOf, rows: Math.max(1, ends.length) };
}

/**
 * One lane per agent (as its team knows it, in its repository) or per
 * repository, most recently active first; within a tie, by name.
 * `opsManagers` names agents known to manage a team; the name rule above
 * catches the rest.
 */
export function buildLanes(
  runs: ReadonlyArray<TimelineRun>,
  by: LanesBy,
  now: number,
  options: { opsManagers?: ReadonlySet<string>; showRepository?: boolean; packGapMs?: number; planToneOf?: (planId: string) => number } = {},
): TimelineLane[] {
  // One lane per agent however its name was written: a run rebuilt from history (D108) may spell it as the session
  // did ("scribe"), the ledger as the team does ("Scribe"); both are one member, in one lane.
  const groups = new Map<string, TimelineRun[]>();
  for (const r of runs) {
    const key = by === 'agent' ? `${r.rootId}\u0000${r.teamId}\u0000${agentKey(r.agent)}` : r.rootId;
    const list = groups.get(key);
    if (list) list.push(r);
    else groups.set(key, [r]);
  }
  const lanes: TimelineLane[] = [];
  for (const [, list] of groups) {
    // The lane is named by a recorded run when it has one: the ledger has the name as the team knows it.
    const first = list.find((r) => !isRebuiltRun(r)) ?? list[0];
    const key = by === 'agent' ? `${first.rootId}\u0000${first.teamId}\u0000${first.agent}` : first.rootId;
    const lastAt = Math.max(...list.map((r) => runSpan(r, now).end));
    const { rowOf, rows } = packRows(list, now, options.packGapMs ?? 0);
    if (by === 'agent') {
      const team = first.teamId === 'solo' ? 'Specialist' : (first.teamName ?? first.teamId);
      // A crew member's lane: its own lane (the key keeps it apart), the member's name, its plan's dot (D178).
      const crewKey = splitCrewKey(first.agent);
      const member = crewKey?.member ?? first.agent;
      const crewPlanId = crewKey ? list.find((r) => r.plan?.id)?.plan?.id : undefined;
      const crew = crewKey ? { planRef: crewKey.planRef, ...(crewPlanId ? { planId: crewPlanId, ...(options.planToneOf ? { colorIndex: options.planToneOf(crewPlanId) } : {}) } : {}) } : undefined;
      const sub = options.showRepository ? `${team} · ${first.repositoryName}` : team;
      lanes.push({ key, label: member, sub: crew ? `${sub} · ${crew.planRef}` : sub, ops: options.opsManagers?.has(first.agent) || options.opsManagers?.has(member) || isOpsManagerName(member), runs: list, rowOf, rows, lastAt, ...(crew ? { crew } : {}) });
    } else {
      const agents = new Set(list.map((r) => r.agent));
      lanes.push({ key, label: first.repositoryName, sub: plural(agents.size, 'agent'), ops: false, runs: list, rowOf, rows, lastAt });
    }
  }
  return lanes.sort((a, b) => b.lastAt - a.lastAt || a.label.localeCompare(b.label));
}

/** A lane's name as a key: case, spaces and punctuation aside ("Scribe" and "scribe" are one agent). */
function agentKey(name: string): string {
  return name.trim().toLowerCase().replace(/[^\p{L}\p{N}·]+/gu, '-').replace(/^-+|-+$/g, '') || name;
}

export interface LaneMetrics {
  /** Height of one row of bars. */
  rowHeight: number;
  /** Bar height inside a row. */
  barHeight: number;
  /** Padding above and below a lane's rows. */
  pad: number;
  /** The commit band under a lane's bars ({@link TimelineLane.commitBand}). */
  band: number;
}

export const LANE_METRICS: LaneMetrics = { rowHeight: 22, barHeight: 14, pad: 7, band: 14 };
export const COMPACT_METRICS: LaneMetrics = { rowHeight: 16, barHeight: 10, pad: 4, band: 12 };

export function laneHeight(lane: Pick<TimelineLane, 'rows' | 'commitBand'>, m: LaneMetrics): number {
  return lane.rows * m.rowHeight + 2 * m.pad + (lane.commitBand ? m.band : 0);
}

/** The top of a bar in a row of a lane (px from the lane's top). */
export function barTop(row: number, m: LaneMetrics): number {
  return m.pad + row * m.rowHeight + (m.rowHeight - m.barHeight) / 2;
}

/**
 * Where a lane's commit markers sit (px from its top): in the band under the
 * bars when the lane has runs, so no marker covers a bar however short the
 * run; across the middle of a lane of commits only.
 */
export function commitMarkerY(lane: Pick<TimelineLane, 'rows' | 'commitBand'>, m: LaneMetrics): number {
  return lane.commitBand ? m.pad + lane.rows * m.rowHeight + m.band / 2 : laneHeight(lane, m) / 2;
}

/** Each lane's top, and the whole height. */
export function laneOffsets(lanes: ReadonlyArray<Pick<TimelineLane, 'rows' | 'commitBand'>>, m: LaneMetrics): { tops: number[]; total: number } {
  const tops: number[] = [];
  let y = 0;
  for (const l of lanes) {
    tops.push(y);
    y += laneHeight(l, m);
  }
  return { tops, total: y };
}

/** The lanes to draw for a scroll position: every lane up to {@link VIRTUALISE_AFTER}, else those in view and `overscan` either side. */
export function visibleLaneRange(tops: readonly number[], total: number, scrollTop: number, viewport: number, overscan = 6): { first: number; last: number } {
  const n = tops.length;
  if (n <= VIRTUALISE_AFTER) return { first: 0, last: n - 1 };
  let first = 0;
  while (first < n - 1 && (tops[first + 1] ?? total) <= scrollTop) first++;
  let last = first;
  while (last < n - 1 && tops[last + 1] < scrollTop + viewport) last++;
  return { first: Math.max(0, first - overscan), last: Math.min(n - 1, last + overscan) };
}

export interface BarGeometry {
  x: number;
  w: number;
  /** It began before the window: no rounded start. */
  cutStart: boolean;
  /** It runs on past the window. */
  cutEnd: boolean;
}

/** Where a run's bar is in a lane `width` wide over the window; undefined when none of it is in the window. */
export function barGeometry(run: Pick<WorkRun, 'startedAt' | 'endedAt'>, win: TimeWindow, width: number, now: number, minPx = MIN_BAR_PX): BarGeometry | undefined {
  const { start, end } = runSpan(run, now);
  if (!Number.isFinite(start) || end < win.start || start > win.end) return undefined;
  const s = timeScale(win, width);
  const x0 = s.x(Math.max(start, win.start));
  const x1 = s.x(Math.min(end, win.end));
  let x = x0;
  let w = Math.max(minPx, x1 - x0);
  if (x + w > width) x = Math.max(0, width - w);
  if (w > width) w = width;
  return { x, w, cutStart: start < win.start, cutEnd: end > win.end };
}

/** A label for a bar `w` wide: whole, cut short with an ellipsis, or nothing when not even a few letters fit. */
export function fitLabel(text: string, w: number, charPx = 6.4, padPx = 10): string {
  const room = Math.floor((w - padPx) / charPx);
  if (room < 4) return '';
  if (text.length <= room) return text;
  return `${text.slice(0, room - 1).trimEnd()}…`;
}

/** Labels that do not run into each other along one row: a label is dropped when the previous one has not ended. */
export function placeLabels(bars: ReadonlyArray<{ id: string; x: number; w: number; text: string }>, charPx = 6.4, padPx = 10): Map<string, string> {
  const out = new Map<string, string>();
  let lastEnd = -Infinity;
  for (const b of [...bars].sort((a, c) => a.x - c.x)) {
    if (b.x < lastEnd + 4) continue;
    const label = fitLabel(b.text, b.w, charPx, padPx);
    if (!label) continue;
    out.set(b.id, label);
    lastEnd = b.x + Math.min(b.w, label.length * charPx + padPx);
  }
  return out;
}

// ── How a bar looks ─────────────────────────────────────────────────────

export type BarLook = 'asked' | 'automation';

/** Two colours: work someone asked for (a person, or an agent handing over) and automation; cancelled is an outline, failed an end mark. */
export function barLook(run: Pick<WorkRun, 'trigger'>): BarLook {
  return run.trigger === 'automation' ? 'automation' : 'asked';
}

/** An entry of the runs' legend. */
export type RunLegendItem = 'asked' | 'automation' | 'cancelled' | 'failed' | 'rebuilt' | 'now';

/**
 * What the runs' legend lists: only what is drawn in the window — a look a
 * bar there has, Cancelled, Failed, Rebuilt when such a bar is there, Now
 * when the Now line is. A timeline of commits only lists no run looks at all.
 */
export function runLegend(runs: ReadonlyArray<Pick<TimelineRun, 'trigger' | 'outcome' | 'startedAt' | 'endedAt'>>, win: TimeWindow, now: number): RunLegendItem[] {
  const seen = new Set<RunLegendItem>();
  for (const r of runs) {
    const { start, end } = runSpan(r, now);
    if (!Number.isFinite(start) || end < win.start || start > win.end) continue;
    if (r.outcome === 'cancelled') seen.add('cancelled');
    else seen.add(barLook(r));
    if (r.outcome === 'error') seen.add('failed');
    if (isRebuiltRun(r)) seen.add('rebuilt');
  }
  if (now >= win.start && now <= win.end) seen.add('now');
  const order: RunLegendItem[] = ['asked', 'automation', 'cancelled', 'failed', 'rebuilt', 'now'];
  return order.filter((i) => seen.has(i));
}

// ── Filter chips ────────────────────────────────────────────────────────

export type TriggerChip = 'all' | RunTrigger;
export type OutcomeChip = 'all' | RunOutcome | 'live';

export interface TimelineChips {
  trigger: TriggerChip;
  outcome: OutcomeChip;
  team: string;
  roadmapOnly: boolean;
}

export const NO_CHIPS: TimelineChips = { trigger: 'all', outcome: 'all', team: 'all', roadmapOnly: false };

export const TRIGGER_CHIPS: ReadonlyArray<{ id: TriggerChip; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'person', label: 'Asked' },
  { id: 'agent', label: 'Handed over' },
  { id: 'automation', label: 'Automation' },
];

export const OUTCOME_CHIPS: ReadonlyArray<{ id: OutcomeChip; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'ok', label: 'Finished' },
  { id: 'error', label: 'Failed' },
  { id: 'cancelled', label: 'Cancelled' },
];

export interface ChipOptions {
  /** The triggers present, in the order above, with "All" first; empty when there is only one kind (nothing to choose). */
  triggers: ReadonlyArray<{ id: TriggerChip; label: string; count: number }>;
  outcomes: ReadonlyArray<{ id: OutcomeChip; label: string; count: number }>;
  /** Teams present, by name; empty when there is one. */
  teams: ReadonlyArray<{ id: string; label: string; count: number }>;
  /** How many runs serve a roadmap item. */
  withRoadmap: number;
}

/** What the chips can offer, from the runs there are: no chip for something none of them is. */
export function chipOptions(runs: ReadonlyArray<TimelineRun>): ChipOptions {
  const count = <K extends string>(key: (r: TimelineRun) => K | undefined) => {
    const m = new Map<K, number>();
    for (const r of runs) {
      const k = key(r);
      if (k !== undefined) m.set(k, (m.get(k) ?? 0) + 1);
    }
    return m;
  };
  const byTrigger = count((r) => r.trigger);
  const byOutcome = count((r) => r.outcome);
  const byTeam = count((r) => r.teamId);
  const teamName = new Map(runs.map((r) => [r.teamId, r.teamId === 'solo' ? 'Specialists' : (r.teamName ?? r.teamId)]));
  const triggers = byTrigger.size > 1 ? TRIGGER_CHIPS.filter((c) => c.id === 'all' || byTrigger.has(c.id as RunTrigger)).map((c) => ({ ...c, count: c.id === 'all' ? runs.length : (byTrigger.get(c.id as RunTrigger) ?? 0) })) : [];
  const outcomes = byOutcome.size > 1 || (byOutcome.size === 1 && byOutcome.get([...byOutcome.keys()][0]) !== runs.length) ? OUTCOME_CHIPS.filter((c) => c.id === 'all' || byOutcome.has(c.id as RunOutcome)).map((c) => ({ ...c, count: c.id === 'all' ? runs.length : (byOutcome.get(c.id as RunOutcome) ?? 0) })) : [];
  const teams = byTeam.size > 1 ? [{ id: 'all', label: 'Every team', count: runs.length }, ...[...byTeam].map(([id, n]) => ({ id, label: teamName.get(id) ?? id, count: n })).sort((a, b) => a.label.localeCompare(b.label))] : [];
  return { triggers, outcomes, teams, withRoadmap: runs.filter((r) => (r.roadmap ?? []).length > 0).length };
}

export function applyChips(runs: ReadonlyArray<TimelineRun>, chips: TimelineChips): TimelineRun[] {
  return runs.filter(
    (r) =>
      (chips.trigger === 'all' || r.trigger === chips.trigger) &&
      (chips.outcome === 'all' || (chips.outcome === 'live' ? r.live : r.outcome === chips.outcome)) &&
      (chips.team === 'all' || r.teamId === chips.team) &&
      (!chips.roadmapOnly || (r.roadmap ?? []).length > 0),
  );
}

/** "Handed over · Failed · Only with a roadmap item": what the chips cut, in words; empty with none on. */
export function chipsLabel(chips: TimelineChips, options: ChipOptions): string {
  const parts: string[] = [];
  if (chips.trigger !== 'all') parts.push(TRIGGER_CHIPS.find((c) => c.id === chips.trigger)?.label ?? '');
  if (chips.outcome !== 'all') parts.push(OUTCOME_CHIPS.find((c) => c.id === chips.outcome)?.label ?? '');
  if (chips.team !== 'all') parts.push(options.teams.find((t) => t.id === chips.team)?.label ?? chips.team);
  if (chips.roadmapOnly) parts.push('only with a roadmap item');
  return parts.filter(Boolean).join(' · ');
}

// ── A run in words ──────────────────────────────────────────────────────

export interface RunDetails {
  title: string;
  /** "Asked by a person · Ada", "Handed over by an agent · Ops", "Automation · Nightly review". */
  trigger: string;
  /** "Finished", "Failed", "Cancelled", "Still going". */
  outcome: string;
  repository: string;
  team: string;
  /** "T-3 Payment intents endpoint". */
  task?: string;
  /** "Checkout plan · step t2". */
  plan?: string;
  roadmap: ReadonlyArray<{ key: string; rootId: string; id: string; text: string; delegated: boolean }>;
  /** "42m", "1h 05m"; "12m so far" while it is still going. */
  duration: string;
  /** "1.2M tokens" (input and output), or "no tokens recorded". */
  tokens: string;
  when: string;
  model?: string;
  /** "Finished on azure:gpt-6-luna (fallback: overloaded)": the turn moved down its class's chain (D312). */
  finishedOn?: string;
  cost?: string;
  /** Rebuilt from the board and the agents' history, not recorded: its times are approximate. */
  rebuilt?: boolean;
}

export function triggerLine(run: Pick<TimelineRun, 'trigger' | 'by' | 'byName'>): string {
  const who = run.trigger === 'person' ? (run.byName ?? run.by?.principal) : run.trigger === 'agent' ? run.by?.actor : (run.by?.routine?.name ?? run.by?.routine?.id);
  return who ? `${TRIGGER_TEXT[run.trigger]} · ${who}` : TRIGGER_TEXT[run.trigger];
}

const FALLBACK_REASON_WORDS: Readonly<Record<string, string>> = { 'rate-limited': 'rate limited', overloaded: 'overloaded', dropped: 'connection dropped', refused: 'refused' };

/**
 * The model a run finished on when its turn moved down its class's fallback chain (D312, the run's `fallbackTo`,
 * read defensively: the run schema is loose). Undefined when it finished where it started.
 */
export function finishedOnLine(run: object): string | undefined {
  const f = (run as { fallbackTo?: unknown }).fallbackTo;
  if (!f || typeof f !== 'object') return undefined;
  const { provider, model, reason } = f as { provider?: unknown; model?: unknown; reason?: unknown };
  if (typeof provider !== 'string' || typeof model !== 'string' || !provider || !model) return undefined;
  const why = typeof reason === 'string' ? (FALLBACK_REASON_WORDS[reason] ?? reason) : 'fallback';
  return `Finished on ${provider}:${model} (fallback: ${why})`;
}

export function runDetails(run: TimelineRun, now: number): RunDetails {
  const { start, end } = runSpan(run, now);
  const tokens = (run.tokens?.input ?? 0) + (run.tokens?.output ?? 0);
  const dur = formatRunTime(Number.isFinite(start) ? end - start : run.durationMs);
  return {
    title: run.title?.trim() || run.task?.title || 'No request recorded',
    trigger: triggerLine(run),
    outcome: run.live ? 'Still going' : run.outcome ? OUTCOME_TEXT[run.outcome] : 'Ended',
    repository: run.repositoryName,
    team: run.teamId === 'solo' ? 'Specialist (no team)' : (run.teamName ?? run.teamId),
    ...(run.task ? { task: `${run.task.id}${run.task.title ? ` ${run.task.title}` : ''}` } : {}),
    ...(run.plan ? { plan: `${run.plan.name ?? run.plan.id}${run.plan.step ? ` · step ${run.plan.step}` : ''}` } : {}),
    roadmap: (run.roadmap ?? []).map((r) => ({ key: `${r.rootId}\u0000${r.id}`, rootId: r.rootId, id: r.id, text: `${r.id}${'title' in r && r.title ? ` ${r.title}` : ''}`, delegated: 'via' in r && r.via === 'delegation' })),
    duration: run.live ? `${dur} so far` : dur,
    tokens: tokens ? `${formatTokenCount(tokens)} tokens` : 'no tokens recorded',
    when: Number.isFinite(start) ? `${formatStamp(start)}${run.live ? '' : ` to ${formatStamp(end).slice(-5)}`}` : '',
    ...(run.model ? { model: run.model } : {}),
    ...(finishedOnLine(run) ? { finishedOn: finishedOnLine(run) } : {}),
    ...(typeof run.costUsd === 'number' ? { cost: formatUsd(run.costUsd) } : {}),
    ...(isRebuiltRun(run) ? { rebuilt: true } : {}),
  };
}

/** One line for a bar's hover and its accessible name. */
export function barSummary(run: TimelineRun, now: number): string {
  const d = runDetails(run, now);
  return `${run.agent}: ${d.title}. ${d.trigger}. ${d.outcome}, ${d.duration}.${d.rebuilt ? ' Rebuilt from history; times are approximate.' : ''}`;
}

// ── Keyboard ────────────────────────────────────────────────────────────

/**
 * The bar the arrows move to: left/right along the lane in time order,
 * up/down to the lane above or below, at the bar nearest in time.
 * With nothing selected, any arrow picks the newest bar of the first lane.
 */
export function moveSelection(lanes: ReadonlyArray<Pick<TimelineLane, 'runs'>>, current: string | undefined, key: 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown', now: number): string | undefined {
  const ordered = lanes.map((l) => [...l.runs].sort((a, b) => runSpan(a, now).start - runSpan(b, now).start));
  if (!ordered.length) return undefined;
  let li = -1;
  let ri = -1;
  if (current) {
    for (let i = 0; i < ordered.length && li < 0; i++) {
      const j = ordered[i].findIndex((r) => r.id === current);
      if (j >= 0) {
        li = i;
        ri = j;
      }
    }
  }
  if (li < 0) {
    const firstLane = ordered.find((l) => l.length);
    return firstLane ? firstLane[firstLane.length - 1].id : undefined;
  }
  const lane = ordered[li];
  if (key === 'ArrowLeft') return lane[Math.max(0, ri - 1)].id;
  if (key === 'ArrowRight') return lane[Math.min(lane.length - 1, ri + 1)].id;
  const at = runSpan(lane[ri], now).start;
  const step = key === 'ArrowUp' ? -1 : 1;
  for (let i = li + step; i >= 0 && i < ordered.length; i += step) {
    if (!ordered[i].length) continue;
    let best = ordered[i][0];
    for (const r of ordered[i]) if (Math.abs(runSpan(r, now).start - at) < Math.abs(runSpan(best, now).start - at)) best = r;
    return best.id;
  }
  return current;
}

// ── Plans in colour (D178) ──────────────────────────────────────────────

/** A crew member's internal key, `Mira·PLAN-2`, split into the member and the plan (`parseCrewKey`, shared/coordination/planIdentity.ts). */
export function splitCrewKey(key: string): { member: string; planRef: string } | undefined {
  return planIdentity.parseCrewKey(key);
}

export interface PlanLegendItem {
  planId: string;
  /** PLAN-2, or the plan's name, else a short tail of its id. */
  label: string;
  colorIndex: number;
}

/**
 * The running plans, for the legend and the bars' colours: the plans with a
 * run going now, only when there are two or more — a team running one plan
 * (or a history of plans that ran one after another) keeps today's colours.
 * In the order they started.
 */
export function planLegend(runs: ReadonlyArray<Pick<TimelineRun, 'plan' | 'startedAt' | 'endedAt' | 'live'>>, toneOf: (planId: string) => number, now: number): PlanLegendItem[] {
  const running = new Map<string, { at: number; name?: string }>();
  for (const r of runs) {
    if (!r.plan?.id || !r.live) continue;
    const at = runSpan(r, now).start;
    const cur = running.get(r.plan.id);
    if (!cur) running.set(r.plan.id, { at, name: r.plan.name });
    else {
      if (at < cur.at) cur.at = at;
      if (!cur.name && r.plan.name) cur.name = r.plan.name;
    }
  }
  if (running.size < 2) return [];
  // A finished run of a running plan names it too, when the live one does not.
  for (const r of runs) {
    const cur = r.plan?.id ? running.get(r.plan.id) : undefined;
    if (cur && !cur.name && r.plan?.name) cur.name = r.plan.name;
  }
  return [...running.entries()]
    .sort((a, b) => a[1].at - b[1].at || a[0].localeCompare(b[0]))
    .map(([planId, v]) => ({ planId, label: v.name ?? `Plan ${planId.replace(/^P-/, '').slice(-6)}`, colorIndex: toneOf(planId) }));
}

/** At most this many plans are named in the legend; the rest still colour their bars. */
export const PLAN_LEGEND_MAX = 6;

/**
 * The colour a bar is drawn in: its plan's slot while the legend shows the
 * running plans and it is one of theirs, `none` for any other run beside them
 * (drawn neutral, so it cannot pass for a running plan), and undefined —
 * today's colours — when there is no legend.
 */
export function barPlanTone(run: Pick<TimelineRun, 'plan'>, legend: readonly PlanLegendItem[]): number | 'none' | undefined {
  if (!legend.length) return undefined;
  const item = run.plan?.id ? legend.find((p) => p.planId === run.plan!.id) : undefined;
  return item ? item.colorIndex : 'none';
}
